import {includes} from '@shared/helpers/collection'
import {
    isValid,
    formatDateFns,
    getDateInfo,
    getDday,
    isBeforeDate,
    isTomorrow,
    makeDateFromRawFormat,
    isToday,
} from '@shared/helpers/date'
import {eq} from '@shared/helpers/lang'

import {
    DeliveryAttributeCodeType,
    DeliveryTagType,
    DeliveryTagTypeValues,
    Entry,
    EntryType,
    MaxLength,
    PickupLocationNecessityType,
    PickupLocationNecessityTypeValues,
} from '@web/core/constants'

import {
    isArrivalGuarantee as isArrivalGuaranteeByUtil,
    isHopeSellerGuarantee as isHopeSellerGuaranteeByUtil,
} from './helpers/commonUtils'

/** 배송 icon 타입 */
export const DeliveryIconType = {
    /** N배송 > 새벽배송 */
    DAWN: 'DAWN',
    /** N희망일배송 */
    HOPE: 'HOPE',
    /** N배송 > D+2이상 배송 */
    STANDARD: 'STANDARD',
    /** N배송 > 일요배송 */
    SUNDAY: 'SUNDAY',
    /** N배송 > 오늘배송 */
    TODAY: 'TODAY',
    /** N배송 > 내일배송 */
    TOMORROW: 'TOMORROW',
    /** 구 도착보장. 3/12일 0시 이전 도착보장상품 주문 시 이 태그 노출 */
    OLD: 'OLD',
} as const

export type DeliveryIconTypeValues = Constant<typeof DeliveryIconType>

/**
 * 배송지목록에 추가 여부
 * 입력한 배송지를 기본배송지로 선택여부
 * --- 위 2가지 여부를 관리하는 정책 ---
 * 주문서 조회 시 실행
 */
export function getInitialDeliveryCheck({isGifting, hasAnyAddress}: {isGifting: boolean; hasAnyAddress: boolean}): {
    isAddDeliveryBookChecked: boolean
    isDefaultDeliveryPlaceChecked: boolean
} {
    return {
        isAddDeliveryBookChecked: !isGifting && !hasAnyAddress,
        isDefaultDeliveryPlaceChecked: !isGifting && !hasAnyAddress,
    }
}

export const getArrivalGuaranteeText = ({
    date,
    deliveryTagType,
    postfix,
}: {
    date?: string
    deliveryTagType?: DeliveryTagTypeValues
    postfix: string
}) => {
    if (!date) {
        return ''
    }

    const isDawnDelivery = eq(deliveryTagType, DeliveryTagType.DAWN)

    /**
     * 새벽배송은 `X시 전` 이 추가된다.
     */
    const dateForm = isDawnDelivery ? 'M.d.(EEE) HH' : 'M.d.(EEE)'
    const dateText = `${formatDateFns(date, dateForm)}${isDawnDelivery ? '시 전' : ''}`

    return [dateText, postfix].join(' ')
}

/** 도착보장 여부 (네이버 도착보장 + 판매자 도착보장 + 희망일 도착보장) */
export const isArrivalGuarantee = isArrivalGuaranteeByUtil

/** 희망일 도착보장 여부 */
export const isHopeSellerGuarantee = isHopeSellerGuaranteeByUtil

/**
 * (구)도착보장 태그 노출 기준일. 해당 날짜 이전 주문건은 구 도착보장 테그 노출
 * https://oss.fin.navercorp.com/Product-Planning/Order_Plan/issues/2288
 * */
const OldArrivalGuaranteeBaseDateTime = 1741705200000 // '2025-03-12:00:00:00'

/**
 * 배송태그를 결정
 * 2025.07.23 새벽배송 대응 이전 주문건에 대해서는 deliveryTag를 직접 계산
 * 이후 주문건에 대해서는 NFA에서 내려받은 deliveryTag를 바로 리턴
 * see) https://wiki.fin.navercorp.com/spaces/FIN/pages/242617381
 * */
export const getDeliveryTag = ({
    orderDate,
    arrivalDate,
    deliveryAttributeType,
    deliveryTagType,
}: {
    orderDate?: string | number
    arrivalDate?: string | number
    deliveryAttributeType?: DeliveryAttributeCodeType
    deliveryTagType?: DeliveryTagTypeValues
}): DeliveryTagTypeValues | undefined => {
    // 도착보장 태그를 BE에서 받은 경우는 바로 사용.
    if (deliveryTagType) {
        return deliveryTagType
    }

    // 도착보장 태그가 없는 경우 (구) 계산 로직을 따름.
    if (!orderDate || !arrivalDate || !deliveryAttributeType) {
        return undefined
    }

    const orderDateTime = new Date(orderDate).getTime()
    const arrivalDateTime = new Date(arrivalDate).getTime()

    const arrivalDday = getDday({targetDay: arrivalDateTime, fromDay: orderDateTime})
    const arrivalDateInfo = getDateInfo(arrivalDateTime)

    const isArrivalGuaranteeProduct = isArrivalGuarantee(deliveryAttributeType)
    const isHopeSellerGuaranteeProduct = isHopeSellerGuarantee(deliveryAttributeType)

    // 날짜형식이 맞지 않거나 주문일보다 도착일이 빠른 경우 도착보장 태그 미노출.
    if (
        !isValid(arrivalDateTime) ||
        !isValid(orderDateTime) ||
        !isArrivalGuaranteeProduct ||
        isBeforeDate({date: arrivalDateTime, dateToCompare: orderDateTime})
    ) {
        return undefined
    }

    // 판매자희망일도착보장은 날짜에 관계없이 희망일 도착보장 태그 노출. 우선순위 1
    if (isHopeSellerGuaranteeProduct) {
        return DeliveryIconType.HOPE
    }

    // 도착일이 주문일과 같으면 TODAY 태그 노출. 우선순위 2
    if (arrivalDday === 0) {
        return DeliveryIconType.TODAY
    }

    // 도착일이 일요일이면 WEEKEND 태그 노출. 우선순위 3
    if (arrivalDateInfo.weekDay === 'SUNDAY') {
        return DeliveryIconType.SUNDAY
    }

    // 도착일이 내일이면 NEXT_DAY 태그 노출. 우선순위 4
    if (arrivalDday === 1) {
        return DeliveryIconType.TOMORROW
    }

    // 그 외(도착일이 주문과 2일 이상 차이) GUARANTEE 태그 노출. 우선순위 5
    if (arrivalDday > 1) {
        return DeliveryIconType.STANDARD
    }

    return undefined
}

/**
 * deliveryTagType을 보고 deliveryIcon을 구하는 정책
 * Standard 일때만 주문시간을 보고 구 도착보장인지 아닌지 판단한다.
 */
export const getDeliveryIconType = ({
    deliveryTagType,
    orderDate,
}: {
    deliveryTagType?: DeliveryTagTypeValues
    orderDate?: string | number
}): Maybe<DeliveryIconTypeValues> => {
    if (!orderDate || !deliveryTagType) {
        return null
    }

    const orderDateTime = new Date(orderDate).getTime()
    const shouldRenderOldIcon =
        isValid(orderDateTime) && isBeforeDate({date: orderDateTime, dateToCompare: OldArrivalGuaranteeBaseDateTime})

    switch (deliveryTagType) {
        case DeliveryTagType.DAWN:
            return DeliveryIconType.DAWN
        case DeliveryTagType.HOPE:
            return DeliveryIconType.HOPE
        case DeliveryTagType.STANDARD:
            return shouldRenderOldIcon ? DeliveryIconType.OLD : DeliveryIconType.STANDARD
        case DeliveryTagType.SUNDAY:
            return DeliveryIconType.SUNDAY
        case DeliveryTagType.TODAY:
            return DeliveryIconType.TODAY
        case DeliveryTagType.TOMORROW:
            return DeliveryIconType.TOMORROW
        default:
            return DeliveryIconType.STANDARD
    }
}

/** 통관고유부호 유효성 체크 */
export const checkPersonalCustomsCode = (code: string): boolean => {
    return !!(code && code.length === 13 && code[0] === 'P' && /\d+/g.exec(code)?.[0]?.length === 12)
}

/** 통관고유부호 연락처 일치 체크 */
export const checkPersonalCustomsClearanceAddress = (
    {
        name,
        phone,
    }: {
        name: string
        phone: string
    },
    {
        receiverName,
        telNo1,
    }: {
        receiverName: string
        telNo1: string
    } = {
        receiverName: '',
        telNo1: '',
    },
): boolean => {
    return !!(name && phone && receiverName && telNo1 && name === receiverName && phone === telNo1)
}

export const checkMemoIncludesInvisibleExceptSpace = (memo: string) => {
    // 참고 BE 필터링 로직:
    // https://oss.fin.navercorp.com/payOrder/order-sheet/blob/d7536a4dec70b800a46fc082a4a527759d88c6f3/support/src/main/java/com/naver/fin/support/util/StringSupports.java#L34
    const INVISIBLE_EXCEPT_SPACE = new RegExp(
        [
            '[\u0000-\u0008]', // U+0000 ~ U+0008 (탭 이전의 제어 문자)
            '[\u000b-\u000c]', // U+000B ~ U+000C (라인 피드와 줄바꿈 이후)
            '[\u000e-\u001f]', // U+000E ~ U+001F (남은 제어 문자)
            '[\u007f-\u00a0]', // 추가 제어 문자
            '\u00ad', // 소프트 하이픈
            '[\u0600-\u0603]', // 아랍어 제어 문자
            '[\u06dd\u070f]', // 추가 아랍어 서식 문자
            '\u1680', // Ogham space mark
            '[\u17b4\u17b5]', // 크메르 서식 문자
            '\u180e', // 몽골 모음 구분자
            '[\u2000-\u200f]', // 일반 공백 및 서식 문자
            '[\u2028\u2029]', // 라인과 문단 구분자 (U+2028-라인, U+2029-문단). U+202F는 Narrow No-Break Space로 별도 포함.
            '[\u205f-\u2064]', // 추가 공백 및 서식 문자들
            '[\u206a-\u206f]', // 추가 양방향 제어 문자
            '\u3000', // 전각 공백
            '[\ud800-\uf8ff]', // 개인용 사용자 영역
            '[\ufeff\ufff9-\ufffb]', // BOM 및 기타 제어 문자
        ].join('|'),
    )

    // 테스트용 캐릭터 💖
    return INVISIBLE_EXCEPT_SPACE.test(memo)
}

export const checkMemoLength = (memo: string) => {
    return memo.length > MaxLength.DELIVERY_MEMO
}

/** 오늘배송, 새벽배송은 수령위치가 필수 */
export const PickupLocationEssentialDeliveryTagTypes = [DeliveryTagType.DAWN, DeliveryTagType.TODAY]

/** 수령위치 필수/선택/필요없음 여부 결정 */
export const getDeliveryInstructionNecessityType = ({
    deliveryTagTypes = [],
    hasArrivalGuaranteeProduct,
    entry,
}: {
    deliveryTagTypes?: DeliveryTagTypeValues[]
    hasArrivalGuaranteeProduct: boolean
    entry: Maybe<EntryType>
}): PickupLocationNecessityTypeValues => {
    /** 장보기는 필수 */
    if (entry === Entry.BRANCH) {
        return PickupLocationNecessityType.ESSENTIAL
    }

    /** N배송인 경우 주문건에 보유한 deliveryTagType 중 수령위치가 필수인 tag가 있으면 필수 아니면 선택 */
    if (hasArrivalGuaranteeProduct) {
        /** 정기구독은 다음 구독일자에 수령위치가 필수인 N배송이 될 수도 있으므로 무조건 필수  */
        if (entry === Entry.SUBSCRIPTION) {
            return PickupLocationNecessityType.ESSENTIAL
        }

        const hasPickupLocationEssentialDeliveryTagType = deliveryTagTypes.some((deliveryTagType) => {
            return includes(deliveryTagType, PickupLocationEssentialDeliveryTagTypes)
        })

        return hasPickupLocationEssentialDeliveryTagType
            ? PickupLocationNecessityType.ESSENTIAL
            : PickupLocationNecessityType.OPTIONAL
    }

    return PickupLocationNecessityType.NONE
}

/**
 * 장보기 배송정보 변경 시 노출되어야 하는 날짜
 * 값이 invalid 하면 undefined 처리
 * @see https://wiki.fin.navercorp.com/x/5Le7E
 */
export const getBranchDeliveryChangeMessage = ({
    updated,
}: {
    /** 변경된 배송정보 */
    updated?: {
        date: string // 20250822,
        time: string // 0700,
    }
}): string | undefined => {
    const date = makeDateFromRawFormat({date: updated?.date, time: updated?.time})
    if (!updated || !date || !isValid(date)) {
        return undefined
    }

    const formattedDate = formatDateFns(date, 'M. d. (EEE)')
    const days = formattedDate?.split(' ')?.[2]
    if (!formattedDate || !days) {
        return undefined
    }

    if (isTomorrow(date)) {
        return `내일${days}`
    }
    if (isToday(date)) {
        return `오늘${days}`
    }
    return `${formattedDate}`
}
